import { z } from 'zod';

/** Request-mapping document (spec §4.1), stored on `endpoints.mapping`. */

export const MAX_MAPPING_RULES = 100;
export const MAX_REGEX_LENGTH = 512;
export const SDK_OPTIONS = ['threadId', 'resourceId', 'temperature', 'maxTokens', 'model'] as const;
export const sdkOptionSchema = z.enum(SDK_OPTIONS);
export type SdkOption = z.infer<typeof sdkOptionSchema>;

const ruleId = z.string().regex(/^[a-zA-Z0-9_-]{1,64}$/, 'Use 1–64 letters, digits, - or _');
const name = z.string().min(1).max(200);
const jsonPathSchema = z
  .string()
  .max(500)
  .regex(
    /^\$(\.[A-Za-z_][A-Za-z0-9_-]*|\[\d+\])*$/,
    'Use a path like $.customer.id or $.items[0].sku',
  );
const varName = z.string().regex(/^[A-Za-z_][A-Za-z0-9_]{0,63}$/, 'Use letters, digits and _');
export const envKeySchema = z
  .string()
  .regex(/^[A-Z_][A-Z0-9_]*$/, 'Use UPPER_CASE letters, digits and _')
  .max(128);

export const mappingSourceSchema = z.discriminatedUnion('from', [
  z.object({ from: z.literal('header'), key: z.string().min(1).max(200) }),
  z.object({ from: z.literal('query'), key: name }),
  z.object({ from: z.literal('body'), path: jsonPathSchema }),
  z.object({ from: z.literal('pathParam'), key: name }),
  z.object({ from: z.literal('apiKey.metadata'), path: jsonPathSchema }),
  z.object({ from: z.literal('env'), key: envKeySchema }),
  z.object({ from: z.literal('const'), value: z.unknown() }),
]);
export type MappingSource = z.infer<typeof mappingSourceSchema>;

export const mappingTransformSchema = z.discriminatedUnion('op', [
  z.object({ op: z.literal('trim') }),
  z.object({ op: z.literal('lowercase') }),
  z.object({ op: z.literal('uppercase') }),
  z.object({ op: z.literal('stripPrefix'), value: z.string().min(1).max(200) }),
  z.object({
    op: z.literal('regexExtract'),
    pattern: z
      .string()
      .min(1)
      .max(MAX_REGEX_LENGTH, `Patterns are limited to ${MAX_REGEX_LENGTH} characters`),
    group: z.number().int().min(0).max(20).default(1),
  }),
  z.object({ op: z.literal('jsonParse') }),
  z.object({
    op: z.literal('template'),
    value: z
      .string()
      .max(2000)
      .refine((v) => v.includes('{{value}}'), {
        message: 'The template must contain {{value}}',
      }),
  }),
  z.object({ op: z.literal('default'), value: z.unknown() }),
  z.object({ op: z.literal('required') }),
  z.object({ op: z.literal('cast'), type: z.enum(['string', 'number', 'boolean']) }),
]);
export type MappingTransform = z.infer<typeof mappingTransformSchema>;

export const mappingTargetSchema = z.discriminatedUnion('to', [
  z.object({ to: z.literal('toolVar'), tool: name, param: name }),
  z.object({
    to: z.literal('mcp'),
    server: name,
    field: z
      .string()
      .regex(
        /^(headers\.[A-Za-z0-9-]{1,100}|meta\.[A-Za-z0-9_.-]{1,100})$/,
        'Use headers.<Name> or meta.<key>',
      ),
  }),
  z.object({ to: z.literal('sdk'), option: sdkOptionSchema }),
  z.object({ to: z.literal('env'), key: envKeySchema }),
  z.object({ to: z.literal('prompt'), var: varName }),
  z.object({ to: z.literal('input') }),
]);
export type MappingTarget = z.infer<typeof mappingTargetSchema>;

export const mappingRuleSchema = z.object({
  id: ruleId,
  label: z.string().max(200).optional(),
  source: mappingSourceSchema,
  transforms: z.array(mappingTransformSchema).max(20).default([]),
  target: mappingTargetSchema,
  secret: z.boolean().default(false),
});
export type MappingRule = z.infer<typeof mappingRuleSchema>;

/** Env keys a mapping may never set: they would change how processes run or leak provider keys. */
export function isReservedEnvKey(key: string): boolean {
  return (
    ['PATH', 'HOME', 'NODE_OPTIONS', 'USER', 'SHELL'].includes(key) ||
    key.startsWith('LD_') ||
    key.startsWith('DYLD_') ||
    key.endsWith('_API_KEY') ||
    key.startsWith('ANTHROPIC_') ||
    key.startsWith('CLAUDE_')
  );
}

/** Names that change how a process, its interpreter or its launcher starts (code injection). */
const PROCESS_HIJACK_KEYS = new Set([
  'PATH',
  'HOME',
  'USER',
  'SHELL',
  'IFS',
  'ENV',
  'CDPATH',
  'GCONV_PATH',
  'TMPDIR',
  'NODE_OPTIONS',
  'NODE_PATH',
  'NODE_EXTRA_CA_CERTS',
  'JAVA_TOOL_OPTIONS',
  '_JAVA_OPTIONS',
  'JDK_JAVA_OPTIONS',
  'CLASSPATH',
  'UV_INDEX_URL',
  'UV_EXTRA_INDEX_URL',
  'PIP_INDEX_URL',
  'PIP_EXTRA_INDEX_URL',
  'SSL_CERT_FILE',
  'SSL_CERT_DIR',
]);
/** Whole families: dynamic linker, shells, interpreters, package managers, git, docker. */
const PROCESS_HIJACK_PREFIXES =
  /^(LD_|DYLD_|BASH_|ZSH_|PYTHON|PERL|RUBY|NPM_CONFIG_|YARN_|PNPM_|COREPACK_|BUN_|DENO_|GIT_|DOCKER_|COMPOSE_|UV_|PIP_)/;

/**
 * Variables an MCP server's own process may not receive: the ones that change how a process, its
 * interpreter or its launcher (`npx`, `uvx`, `python3`, `docker`, …) starts — any of them can run
 * code the admin did not choose. Unlike `isReservedEnvKey`, keys like `BRAVE_API_KEY` are allowed:
 * they only reach that server's process, which otherwise gets just the MCP SDK's
 * defaults (HOME, LOGNAME, PATH, SHELL, TERM, USER) — never the API's own secrets.
 */
export function isReservedMcpEnvKey(key: string): boolean {
  const k = key.toUpperCase();
  return PROCESS_HIJACK_KEYS.has(k) || PROCESS_HIJACK_PREFIXES.test(k);
}

export const mappingDocSchema = z
  .object({
    version: z.literal(1),
    rules: z.array(mappingRuleSchema).max(MAX_MAPPING_RULES),
  })
  .superRefine((doc, ctx) => {
    const seen = new Set<string>();
    doc.rules.forEach((rule, i) => {
      if (seen.has(rule.id)) {
        ctx.addIssue({
          code: 'custom',
          path: ['rules', i, 'id'],
          message: `Duplicate rule id "${rule.id}"`,
        });
      }
      seen.add(rule.id);
      if (rule.secret && rule.target.to === 'prompt') {
        ctx.addIssue({
          code: 'custom',
          path: ['rules', i, 'secret'],
          message: 'Secret values cannot be put into the prompt',
        });
      }
      if (rule.target.to === 'env' && isReservedEnvKey(rule.target.key)) {
        ctx.addIssue({
          code: 'custom',
          path: ['rules', i, 'target', 'key'],
          message: `${rule.target.key} is reserved and cannot be set by a mapping`,
        });
      }
    });
  });
export type MappingDoc = z.infer<typeof mappingDocSchema>;

export const EMPTY_MAPPING: MappingDoc = { version: 1, rules: [] };

export const MAPPING_ERROR_CODES = [
  'missing_required',
  'transform_failed',
  'invalid_target',
  'type_error',
] as const;
export type MappingErrorCode = (typeof MAPPING_ERROR_CODES)[number];
export interface MappingError {
  ruleId: string;
  code: MappingErrorCode;
  message: string;
}
